import type { IDiagnosticReport } from './AdminProcessDetails';
import type { AdminProcess } from './adminProcessDetailsTypes';
import { ReportCategories } from '../../../../common/enums/reportCategories';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { ServiceType } from '../../../../common/enums/serviceType';
import { SupervisionStatus } from '../../../../common/enums/supervisionStatus';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { translateKey } from '../../../translate';
import { AdminProcessNullableFormattedDatetime } from '../../elements/common';
import { FormLabel } from '../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../elements/forms/InputContainer';
import { InlineReadElement } from '../../elements/inlineReadElement/InlineReadElement';

interface IAdminProcessDetailsTimestampsProps {
    adminProcess: AdminProcess;
}

export function AdminProcessDetailsTimestamps(props: IAdminProcessDetailsTimestampsProps) {
    let lastDiagnosticReport: IDiagnosticReport | undefined;

    if (props.adminProcess.diagnosticReports && props.adminProcess.diagnosticReports.length > 0) {
        lastDiagnosticReport = props.adminProcess.diagnosticReports[props.adminProcess.diagnosticReports.length - 1];
    }

    if (props.adminProcess.service.serviceType === ServiceType.examination) {
        return (
            <>
                {requestCreatedAt()}
                {requestDateSent(
                    translateKey('processDetails.requestedAt'),
                    translateKey('processDetails.notYetRequested')
                )}
                {procedureDateSent(
                    translateKey('processDetails.examinedAt'),
                    translateKey('processDetails.notYetExamined')
                )}
                {diagnosticReportDateSent(
                    translateKey('processDetails.gradedAt'),
                    translateKey('processDetails.notYetGraded')
                )}
                {requestClosed(translateKey('processDetails.closedAt'), translateKey('processDetails.notYetClosed'))}
                {supervisionClosed(
                    translateKey('processDetails.supervisedAt'),
                    translateKey('processDetails.notYetSupervised')
                )}
                {requestAbortedOrCancelled(props.adminProcess.status)}
            </>
        );
    }

    if (props.adminProcess.service.serviceType === ServiceType.consultation) {
        return (
            <>
                {requestCreatedAt()}
                {requestDateSent(
                    translateKey('processDetails.requestedAt'),
                    translateKey('processDetails.notYetRequested')
                )}
                {diagnosticReportDateSent(
                    translateKey('processDetails.answeredAt'),
                    translateKey('processDetails.notYetAnswered')
                )}
                {diagnosticReportQuestionSent(
                    translateKey('processDetails.secondRoundQuestionAt'),
                    translateKey('processDetails.notYetSecondRoundQuestion')
                )}
                {diagnosticReportAnsweredSent(
                    translateKey('processDetails.secondRoundAnswerAt'),
                    translateKey('processDetails.notYetSecondRoundAnswer')
                )}
                {requestClosed(translateKey('processDetails.closedAt'), translateKey('processDetails.notYetClosed'))}
                {requestAbortedOrCancelled(props.adminProcess.status)}
            </>
        );
    }

    if (props.adminProcess.service.serviceType === ServiceType.referral) {
        return (
            <>
                {requestCreatedAt()}
                {requestDateSent(translateKey('processDetails.sentAt'), translateKey('processDetails.notYetSent'))}
                {diagnosticReportDateSent(
                    translateKey('processDetails.intakeAt'),
                    translateKey('processDetails.notYetIntake'),
                    ReportCategories.intake
                )}
                {diagnosticReportDateSent(
                    translateKey('processDetails.dischargedAt'),
                    translateKey('processDetails.notYetDischarged'),
                    ReportCategories.discharge
                )}
                {requestAbortedOrCancelled(props.adminProcess.status)}
            </>
        );
    }

    if (props.adminProcess.service.serviceType === ServiceType.referralSmall) {
        return (
            <>
                {requestCreatedAt()}
                {requestDateSent(translateKey('processDetails.sentAt'), translateKey('processDetails.notYetSent'))}
                {requestAbortedOrCancelled(props.adminProcess.status)}
            </>
        );
    }

    if (props.adminProcess.service.serviceType === ServiceType.contact) {
        return (
            <>
                {requestCreatedAt()}
                {requestDateSent(translateKey('processDetails.sentAt'), translateKey('processDetails.notYetSent'))}
                {requestClosed(translateKey('processDetails.closedAt'), translateKey('processDetails.notYetClosed'))}
                {requestAbortedOrCancelled(props.adminProcess.status)}
            </>
        );
    }

    if (props.adminProcess.service.serviceType === ServiceType.invitation) {
        return (
            <>
                {requestInvitationSent(
                    translateKey('StatusLabelEnum.invited'),
                    translateKey('processDetails.notYetInvited')
                )}
                {procedureStarted(
                    translateKey('StatusLabelEnum.started'),
                    translateKey('processDetails.notYetStarted')
                )}
                {requestClosed(translateKey('processDetails.closedAt'), translateKey('processDetails.notYetClosed'))}
                {requestAbortedOrCancelled(props.adminProcess.status)}
            </>
        );
    }

    return null;

    function requestCreatedAt() {
        return displayDate(
            translateKey('processDetails.createdAt'),
            translateKey('processDetails.createdAt'),
            props.adminProcess.created_at
        );
    }

    function requestDateSent(label: string, dateNotSetLabel: string) {
        return displayDate(label, dateNotSetLabel, props.adminProcess.dateSent);
    }

    function procedureDateSent(label: string, dateNotSetLabel: string) {
        const isBioGrader =
            props.adminProcess.procedure &&
            lastDiagnosticReport &&
            props.adminProcess.procedure.performerPractitionerId === lastDiagnosticReport.performerPractitionerId &&
            props.adminProcess.procedure.performerOrganizationId === lastDiagnosticReport.performerOrganizationId;

        if (isBioGrader) {
            return null;
        }

        return displayDate(
            label,
            dateNotSetLabel,
            props.adminProcess.procedure && props.adminProcess.procedure.dateSent
        );
    }

    function diagnosticReportDateSent(label: string, dateNotSetLabel: string, reportCategory?: ReportCategories) {
        let date = lastDiagnosticReport && lastDiagnosticReport.dateSent;
        if (props.adminProcess.diagnosticReports && reportCategory) {
            const report = props.adminProcess.diagnosticReports.find((item) => item.category === reportCategory);
            date = report ? report.dateSent : undefined;
        }

        return displayDate(label, dateNotSetLabel, date);
    }

    function diagnosticReportQuestionSent(label: string, dateNotSetLabel: string) {
        return displayDate(
            label,
            dateNotSetLabel,
            lastDiagnosticReport && lastDiagnosticReport.clarificationQARequesterSentDateTime
        );
    }

    function diagnosticReportAnsweredSent(label: string, dateNotSetLabel: string) {
        return displayDate(
            label,
            dateNotSetLabel,
            lastDiagnosticReport && lastDiagnosticReport.clarificationQAPerformerSentDateTime
        );
    }

    function requestClosed(label: string, dateNotSetLabel: string) {
        return displayDate(label, dateNotSetLabel, props.adminProcess.closedDateTime);
    }

    function supervisionClosed(label: string, dateNotSetLabel: string) {
        return (
            <HorizontalFormGroupRead>
                <FormLabel>{label}</FormLabel>
                <InputContainer>
                    {props.adminProcess.supervision &&
                    props.adminProcess.supervision.status === SupervisionStatus.completed ? (
                        <InlineReadElement bold>
                            <AdminProcessNullableFormattedDatetime value={props.adminProcess.supervision.updated_at} />
                        </InlineReadElement>
                    ) : (
                        <InlineReadElement color="muted">{dateNotSetLabel}</InlineReadElement>
                    )}
                </InputContainer>
            </HorizontalFormGroupRead>
        );
    }

    function requestAbortedOrCancelled(status: RequestStatus) {
        if (![RequestStatus.cancelled, RequestStatus.aborted].includes(status)) {
            return null;
        }

        return (
            <>
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey(`request.status_${status}`)}</FormLabel>
                    <InputContainer>
                        {props.adminProcess.cancellingAbortingDate && (
                            <InlineReadElement bold>
                                <AdminProcessNullableFormattedDatetime
                                    value={props.adminProcess.cancellingAbortingDate}
                                />
                            </InlineReadElement>
                        )}

                        <InlineReadElement>
                            {`${translateKey('common.by')} ${
                                props.adminProcess.cancellingAbortingActor?.id
                                    ? getDisplayName(props.adminProcess.cancellingAbortingActor)
                                    : 'ClientSafe'
                            }`}
                        </InlineReadElement>
                    </InputContainer>
                </HorizontalFormGroupRead>
                <HorizontalFormGroupRead>
                    <FormLabel>
                        {status === RequestStatus.aborted
                            ? translateKey('request.status_reason_aborted')
                            : translateKey('request.status_reason_cancelled')}
                    </FormLabel>
                    <InputContainer>
                        {props.adminProcess.reasonStopProccess && (
                            <InlineReadElement>{props.adminProcess.reasonStopProccess}</InlineReadElement>
                        )}
                    </InputContainer>
                </HorizontalFormGroupRead>
            </>
        );
    }

    function displayDate(label: string, dateNotSetLabel: string, date: string | null | undefined) {
        return (
            <HorizontalFormGroupRead>
                <FormLabel>{label}</FormLabel>
                <InputContainer>
                    {date ? (
                        <InlineReadElement bold>
                            <AdminProcessNullableFormattedDatetime value={date} />
                        </InlineReadElement>
                    ) : (
                        <InlineReadElement color="muted">{dateNotSetLabel}</InlineReadElement>
                    )}
                </InputContainer>
            </HorizontalFormGroupRead>
        );
    }

    function requestInvitationSent(label: string, dateNotSetLabel: string) {
        return displayDate(label, dateNotSetLabel, props.adminProcess.created_at);
    }

    function procedureStarted(label: string, dateNotSetLabel: string) {
        return displayDate(label, dateNotSetLabel, props.adminProcess.procedure?.customControlData?.dateStarted);
    }
}
